/**
 * The Shift Roster's people list: everyone on the plant's crews for one day —
 * free on shift (green, ready to place), on a job, out, or off. Pick a free
 * person (click) and then a batch, or drag the name onto a batch.
 */
import { addDays, format, isSameDay, parseISO } from "date-fns";
import React, { useCallback, useMemo } from "react";

import { PersonDayState, PersonOnDay } from "../../utils/people-day";
import { eyebrowSx, lbb } from "../../utils/tokens";
import { useWorkerChipDrag, WorkerDragItem } from "../manual-controls"; // eslint-disable-line sort-imports

export const PEOPLE_PANEL_WIDTH = 236;

const SECTIONS: { state: PersonDayState; title: string }[] = [
	{ state: "available", title: "Available" },
	{ state: "allocated", title: "On a job" },
	{ state: "out", title: "Out" },
	{ state: "off", title: "Off today" },
];

const clock = (iso: string) => format(parseISO(iso), "HH:mm");
const hours = (start: string, end: string) => `${clock(start)}–${clock(end)}`;

/** The second line under a name: where they are, or when their shift is. */
const statusOf = (person: PersonOnDay): string => {
	if (person.state === "allocated") {
		return person.work.map((w) => `${w.workstation} ${hours(w.start, w.end)}`).join(" · ");
	}
	const home = [person.home, person.shiftLabel].filter(Boolean).join(" · ");
	if (person.state === "available" && person.shift) return `${hours(person.shift.start, person.shift.end)}${home ? ` · ${home}` : ""}`;
	return home || "Not on this plant's crews";
};

const PersonRow: React.FC<{ person: PersonOnDay; picked: boolean; onPick: (person: PersonOnDay) => void }> = ({ person, picked, onPick }) => {
	const { isDragging, dragRef } = useWorkerChipDrag(person.workerId, person.workerName);
	const placeable = person.state === "available" || person.state === "allocated";
	const free = person.state === "available";
	const handleClick = useCallback(() => placeable && onPick(person), [placeable, onPick, person]);
	const handleKey = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				handleClick();
			}
		},
		[handleClick]
	);
	return (
		<div
			ref={placeable ? dragRef : undefined}
			role={placeable ? "button" : undefined}
			tabIndex={placeable ? 0 : undefined}
			aria-pressed={placeable ? picked : undefined}
			onClick={handleClick}
			onKeyDown={handleKey}
			title={placeable ? "Click, then click a batch to place them — or drag the name onto a batch" : undefined}
			style={{
				padding: "6px 8px",
				borderRadius: 4,
				border: picked ? `2px solid ${lbb.navy}` : `1px solid ${free ? lbb.success : lbb.navy20}`,
				background: free ? lbb.successTint : lbb.paper,
				opacity: isDragging ? 0.4 : person.state === "out" || person.state === "off" ? 0.6 : 1,
				cursor: placeable ? "grab" : "default",
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}
		>
			<span style={{ fontFamily: lbb.fontUi, fontSize: 13, fontWeight: 600, color: free ? lbb.success : lbb.navy }}>{person.workerName}</span>
			<span style={{ fontFamily: lbb.fontMono, fontSize: 10.5, color: lbb.ink60, overflowWrap: "anywhere" }}>{statusOf(person)}</span>
		</div>
	);
};

const DayButton: React.FC<{ day: Date; on: boolean; onDay: (day: Date) => void }> = ({ day, on, onDay }) => {
	const handleClick = useCallback(() => onDay(day), [onDay, day]);
	return (
		<button
			type="button"
			aria-pressed={on}
			aria-label={format(day, "EEEE d MMMM")}
			onClick={handleClick}
			style={{
				flex: 1,
				padding: "3px 0",
				border: `1px solid ${on ? lbb.navy : lbb.navy20}`,
				background: on ? lbb.navy : lbb.paper,
				color: on ? lbb.paper : lbb.navy,
				borderRadius: 3,
				fontFamily: lbb.fontUi,
				fontSize: 11,
				fontWeight: 600,
				cursor: "pointer",
			}}
		>
			{format(day, "EEEEE")}
		</button>
	);
};

export const PeoplePanel: React.FC<{
	people: PersonOnDay[];
	weekStart: Date;
	day: Date;
	/** Week view: pick the day the list is for. */
	onDay?: (day: Date) => void;
	picked: WorkerDragItem | null;
	onPick: (person: WorkerDragItem | null) => void;
	onHide: () => void;
}> = ({ people, weekStart, day, onDay, picked, onPick, onHide }) => {
	const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
	const handlePick = useCallback(
		(person: PersonOnDay) => onPick(picked?.workerId === person.workerId ? null : { workerId: person.workerId, workerName: person.workerName }),
		[onPick, picked]
	);
	const cancelPick = useCallback(() => onPick(null), [onPick]);
	return (
		<aside
			aria-label="People"
			style={{ width: PEOPLE_PANEL_WIDTH, flexShrink: 0, borderLeft: lbb.borderHair, background: lbb.paper, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 225px)" }}
		>
			<div style={{ padding: "8px 10px", borderBottom: lbb.borderHair, display: "flex", flexDirection: "column", gap: 6 }}>
				<div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
					<span style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60 }}>People</span>
					<span style={{ fontFamily: lbb.fontBrand, fontSize: 14, fontWeight: 700, color: lbb.navy }}>{format(day, "EEE d MMM")}</span>
					<button type="button" onClick={onHide} style={{ marginLeft: "auto", border: "none", background: "none", color: lbb.ink60, fontFamily: lbb.fontUi, fontSize: 12, cursor: "pointer" }}>
						Hide
					</button>
				</div>
				{onDay && (
					<div role="group" aria-label="Day" style={{ display: "flex", gap: 2 }}>
						{days.map((d) => (
							<DayButton key={d.toISOString()} day={d} on={isSameDay(d, day)} onDay={onDay} />
						))}
					</div>
				)}
				{picked && (
					<div style={{ fontFamily: lbb.fontUi, fontSize: 12, color: lbb.navy }}>
						<strong>{picked.workerName}</strong> picked — click a batch to place them.{" "}
						<button type="button" onClick={cancelPick} style={{ border: "none", background: "none", color: lbb.brick, cursor: "pointer", padding: 0, font: "inherit" }}>
							Cancel
						</button>
					</div>
				)}
			</div>
			<div style={{ overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
				{SECTIONS.map(({ state, title }) => {
					const list = people.filter((p) => p.state === state);
					if (list.length === 0) return null;
					return (
						<section key={state} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
							<span style={{ ...eyebrowSx, fontSize: 10, color: state === "available" ? lbb.success : lbb.ink60 }}>
								{title} · {list.length}
							</span>
							{list.map((person) => (
								<PersonRow key={person.workerId} person={person} picked={picked?.workerId === person.workerId} onPick={handlePick} />
							))}
						</section>
					);
				})}
				{people.length === 0 && <span style={{ fontFamily: lbb.fontUi, fontSize: 12, color: lbb.ink60 }}>No crews found for this plant.</span>}
			</div>
		</aside>
	);
};
